import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CalendarDays, Plus, Pencil, Trash2, X, Save, Upload, Download,
  Loader2, ChevronLeft, RefreshCcw, FileSpreadsheet, BookOpen,
  Hammer, Lightbulb, GraduationCap, CalendarClock, Copy,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return s; }
};

const toDateInputValue = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ""; }
};

// Campos editáveis agrupados por fase pedagógica
const PHASE_FIELDS = [
  { key: "ObjetivosEspecificos", label: "Objetivos Específicos", textarea: true },
  { key: "ConteudosProgramaticos", label: "Conteúdos Programáticos", textarea: true },
  { key: "Metodologia", label: "Metodologia", textarea: true },
  { key: "Recursos", label: "Recursos", textarea: true },
  { key: "Atividades", label: "Atividades", textarea: true },
  { key: "AvaliacaoConhecimentos", label: "Avaliação", textarea: false },
  { key: "Duracao", label: "Duração", textarea: false },
];

const PHASES = [
  { prefix: "Int", title: "Introdução", icon: Lightbulb, tone: "blue" },
  { prefix: "Des", title: "Desenvolvimento", icon: Hammer, tone: "amber" },
  { prefix: "Con", title: "Conclusão", icon: GraduationCap, tone: "green" },
];

export default function AdminCursoSessoes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursoId = searchParams.get("curso") || "";

  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(true);
  const [sessoes, setSessoes] = useState([]);
  const [sessoesLoading, setSessoesLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (new) | {...row}
  const [showImport, setShowImport] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  const [showShift, setShowShift] = useState(false);
  const [showImportFromCurso, setShowImportFromCurso] = useState(false);

  const loadCursos = useCallback(async () => {
    setCursosLoading(true);
    try {
      const r = includePast
        ? await adminApi.cursosListIncludingPast()
        : await adminApi.cursosListActive();
      setCursos(r.data || []);
    } catch (e) { toast.error(e.message || "Erro a carregar cursos"); }
    finally { setCursosLoading(false); }
  }, [includePast]);

  useEffect(() => { loadCursos(); }, [loadCursos]);

  const selectedCurso = useMemo(
    () => cursos.find((x) => String(x.ID_Curso) === String(cursoId)) || null,
    [cursos, cursoId],
  );

  const loadSessoes = useCallback(async () => {
    if (!cursoId) { setSessoes([]); return; }
    setSessoesLoading(true);
    try {
      const r = await adminApi.sessoesList(cursoId);
      setSessoes(r.data || []);
    } catch (e) { toast.error(e.message || "Erro a carregar sessões"); }
    finally { setSessoesLoading(false); }
  }, [cursoId]);

  useEffect(() => { loadSessoes(); }, [loadSessoes]);

  const handleSelectCurso = (e) => {
    const v = e.target.value;
    if (v) setSearchParams({ curso: v });
    else setSearchParams({});
  };

  const handleNew = () => {
    const nextOrdem = (sessoes.reduce((m, s) => Math.max(m, Number(s.Ordem) || 0), 0)) + 1;
    setEditing({ Ordem: nextOrdem });
  };

  const handleDuplicarUltima = () => {
    if (!sessoes.length) { handleNew(); return; }
    const last = sessoes[sessoes.length - 1];
    const { ID_PlanoSessao: _idIgnored, ...rest } = last;
    void _idIgnored;
    setEditing({
      ...rest,
      Ordem: (Number(last.Ordem) || 0) + 1,
      Data: "",
    });
  };

  const handleEdit = (s) => setEditing(s);

  const handleDelete = async (s) => {
    if (!window.confirm(`Apagar a sessão #${s.Ordem} (${fmtDate(s.Data)})?`)) return;
    try {
      await adminApi.sessoesDelete(cursoId, s.ID_PlanoSessao);
      toast.success("Sessão apagada.");
      loadSessoes();
    } catch (e) { toast.error(e.message || "Erro a apagar"); }
  };

  const handleSaved = () => {
    setEditing(null);
    loadSessoes();
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-curso-sessoes">
      <header className="mb-6 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold">
            <Link to="/admin" className="hover:underline">Admin</Link>
            <span>›</span>
            <span>Sessões Pedagógicas</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1B1B1B] flex items-center gap-2 mt-1">
            <CalendarDays className="w-6 h-6 text-[#A8843C]" />
            Plano de Sessões por Curso
          </h1>
          <p className="text-sm text-[#6E6754] mt-1">
            Maintenance completa da tabela <code className="text-[11px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 rounded">curso.Trel_PlanoSessao</code> —
            cada sessão tem Introdução, Desenvolvimento e Conclusão. No mesmo dia podem existir 1 ou 2 sessões.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            href={adminApi.sessoesTemplateUrl()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="btn-download-template"
          >
            <Download className="w-4 h-4" /> Template Excel
          </a>
          <button
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#A8843C] text-[#A8843C] rounded hover:bg-[#FAF8F2]"
            data-testid="btn-import-excel"
          >
            <Upload className="w-4 h-4" /> Importar Excel
          </button>
        </div>
      </header>

      <CursoTabsNav cursoId={cursoId} current="sessoes"
        cursoLabel={selectedCurso ? `${selectedCurso.NomeCurso || selectedCurso.Descricao || `Produto ${selectedCurso.ID_Produto}`} • #${selectedCurso.ID_Curso}` : undefined} />

      {/* Picker de curso */}
      <section className="mb-6 p-4 bg-white border border-[#E6E1D6] rounded-md">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Curso
            </label>
            <select
              value={cursoId}
              onChange={handleSelectCurso}
              disabled={cursosLoading}
              className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white"
              data-testid="curso-picker"
            >
              <option value="">— Escolher curso —</option>
              {cursos.map((c) => (
                <option key={c.ID_Curso} value={c.ID_Curso}>
                  {c.Descricao || `Produto ${c.ID_Produto}`} • #{c.ID_Curso}{c.ID_Produto != null ? ` (Produto #${c.ID_Produto})` : ""} • {c.Local || "—"} • {fmtDate(c.DataInicio)}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#6E6754]">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
              data-testid="toggle-include-past"
            />
            Mostrar cursos passados
          </label>
          {cursoId && (
            <button
              onClick={loadSessoes}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
              data-testid="btn-reload-sessoes"
              disabled={sessoesLoading}
            >
              <RefreshCcw className={`w-4 h-4 ${sessoesLoading ? "animate-spin" : ""}`} /> Recarregar
            </button>
          )}
        </div>
        {selectedCurso && (
          <div className="mt-3 text-[12px] text-[#6E6754] flex gap-2 items-center flex-wrap">
            <BookOpen className="w-3.5 h-3.5" />
            <strong>{selectedCurso.Descricao}</strong>
            <span>·</span>
            <span>{selectedCurso.Local}</span>
            <span>·</span>
            <span>{fmtDate(selectedCurso.DataInicio)} a {fmtDate(selectedCurso.DataFim)}</span>
            <span>·</span>
            <span>{selectedCurso.CargaHoraria || "?"}h</span>
          </div>
        )}
      </section>

      {/* Lista de sessões */}
      {cursoId ? (
        <section className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E6E1D6] bg-[#FAF8F2] flex flex-wrap gap-2 items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1B1B1B]">
              {sessoes.length} sessões neste curso
            </h2>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowImportFromCurso(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#7C3AED] text-[#5B21B6] rounded hover:bg-purple-50"
                data-testid="btn-import-from-curso"
                title="Importar sessões de outro curso já configurado"
              >
                <Copy className="w-3.5 h-3.5" /> Importar de outro curso
              </button>
              <button
                onClick={() => setShowShift(true)}
                disabled={!sessoes.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#3B82F6] text-[#1D4ED8] rounded hover:bg-blue-50 disabled:opacity-40"
                data-testid="btn-shift-dates"
                title="Deslocar todas as datas mantendo os offsets relativos"
              >
                <CalendarClock className="w-3.5 h-3.5" /> Recalcular datas
              </button>
              <button
                onClick={handleDuplicarUltima}
                disabled={!sessoes.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#E6E1D6] rounded hover:bg-white disabled:opacity-40"
                data-testid="btn-duplicate-last"
              >
                <Plus className="w-3.5 h-3.5" /> Duplicar última
              </button>
              <button
                onClick={handleNew}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A]"
                data-testid="btn-new-sessao"
              >
                <Plus className="w-3.5 h-3.5" /> Nova sessão
              </button>
            </div>
          </div>

          {sessoesLoading ? (
            <div className="p-8 text-center text-[#978D78]">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              A carregar…
            </div>
          ) : sessoes.length === 0 ? (
            <div className="p-8 text-center text-[#978D78]">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-60" />
              Nenhuma sessão. Cria a primeira com <strong>Nova sessão</strong> ou <strong>Importar Excel</strong>.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="sessoes-table">
              <thead className="bg-[#FAF8F2] text-[11px] uppercase tracking-wider text-[#978D78]">
                <tr>
                  <th className="px-3 py-2 text-left w-12">#</th>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Horário</th>
                  <th className="px-3 py-2 text-left">Duração</th>
                  <th className="px-3 py-2 text-left">Objetivos Gerais</th>
                  <th className="px-3 py-2 text-left">Sumário</th>
                  <th className="px-3 py-2 text-right w-32">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sessoes.map((s) => (
                  <tr key={s.ID_PlanoSessao} className="border-t border-[#E6E1D6] hover:bg-[#FAF8F2]/50">
                    <td className="px-3 py-2 font-mono text-[#A8843C] font-bold">{s.Ordem}</td>
                    <td className="px-3 py-2">{fmtDate(s.Data)}</td>
                    <td className="px-3 py-2 text-[#3D352B]">
                      {s.HoraSessao || (
                        <span className="text-[#978D78] text-xs">
                          {s.HorarioManha || ""} {s.HorarioTarde ? `· ${s.HorarioTarde}` : ""}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#6E6754]">{s.Duracao || "—"}</td>
                    <td className="px-3 py-2 text-[#6E6754] text-[12px] max-w-[300px] truncate" title={s.ObjetivosGerais || ""}>
                      {s.ObjetivosGerais || "—"}
                    </td>
                    <td className="px-3 py-2 text-[#6E6754] text-[12px] max-w-[300px] truncate" title={s.Sumario || ""}>
                      {s.Sumario || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => handleEdit(s)}
                        className="text-[#A8843C] hover:bg-[#FAF8F2] p-1.5 rounded"
                        data-testid={`btn-edit-${s.ID_PlanoSessao}`}
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        className="text-red-700 hover:bg-red-50 p-1.5 rounded ml-1"
                        data-testid={`btn-delete-${s.ID_PlanoSessao}`}
                        title="Apagar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>
      ) : (
        <div className="p-8 text-center text-[#978D78] bg-white border border-[#E6E1D6] rounded-md">
          Escolhe um curso para gerir as sessões.
        </div>
      )}

      {/* Modal editor */}
      {editing && cursoId && (
        <SessaoEditorModal
          cursoId={cursoId}
          sessao={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Modal import Excel */}
      {showImport && (
        <ImportSessoesModal
          cursoId={cursoId}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); loadSessoes(); }}
        />
      )}

      {/* Modal recalcular datas */}
      {showShift && cursoId && (
        <ShiftDatesModal
          cursoId={cursoId}
          sessoes={sessoes}
          onClose={() => setShowShift(false)}
          onShifted={() => { setShowShift(false); loadSessoes(); loadCursos(); }}
        />
      )}

      {/* Modal importar de outro curso */}
      {showImportFromCurso && cursoId && (
        <ImportFromCursoModal
          cursoId={cursoId}
          existingSessoes={sessoes}
          onClose={() => setShowImportFromCurso(false)}
          onImported={() => { setShowImportFromCurso(false); loadSessoes(); loadCursos(); }}
        />
      )}
    </div>
  );
}


// ── Modal: Importar sessões de outro curso ─────────────────────────
function ImportFromCursoModal({ cursoId, existingSessoes, onClose, onImported }) {
  const [cursos, setCursos] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(true);
  const [sourceId, setSourceId] = useState("");
  const [includePast, setIncludePast] = useState(true);
  const [replace, setReplace] = useState(false);
  const [shiftStart, setShiftStart] = useState("");
  const [preview, setPreview] = useState(null); // {Ordem, Data, ...}[]
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingCursos(true);
    (async () => {
      try {
        const r = includePast
          ? await adminApi.cursosListIncludingPast()
          : await adminApi.cursosListActive();
        if (!cancelled) setCursos((r.data || []).filter((c) => String(c.ID_Curso) !== String(cursoId)));
      } catch (e) { if (!cancelled) toast.error(e.message || "Erro a carregar cursos"); }
      finally { if (!cancelled) setLoadingCursos(false); }
    })();
    return () => { cancelled = true; };
  }, [includePast, cursoId]);

  useEffect(() => {
    let cancelled = false;
    if (!sourceId) { setPreview(null); return; }
    (async () => {
      try {
        const r = await adminApi.sessoesList(sourceId);
        if (!cancelled) setPreview(r.data || []);
      } catch { if (!cancelled) setPreview([]); }
    })();
    return () => { cancelled = true; };
  }, [sourceId]);

  const handleImport = async () => {
    if (!sourceId) { toast.error("Escolhe um curso origem."); return; }
    const total = preview?.length || 0;
    if (!total) { toast.error("O curso origem não tem sessões."); return; }
    const replaceWarning = replace && existingSessoes.length > 0
      ? `\n\n⚠ Vai APAGAR ${existingSessoes.length} sessões existentes do curso destino antes de importar.`
      : "";
    const shiftInfo = shiftStart ? `\n   Datas deslocadas para começar em ${shiftStart}.` : "";
    if (!window.confirm(
      `Importar ${total} sessões do curso #${sourceId} para o curso #${cursoId}?\n`
      + `Modo: ${replace ? "Substituir" : "Adicionar ao final"}${shiftInfo}${replaceWarning}`
    )) return;
    setBusy(true);
    try {
      const r = await adminApi.sessoesImportFromCurso(cursoId, {
        sourceCursoId: Number(sourceId), replace, shiftStart: shiftStart || null,
      });
      toast.success(
        `${r.imported}/${r.source_total} sessões importadas (modo ${r.mode}`
        + `${r.delta_days ? `, ${r.delta_days > 0 ? "+" : ""}${r.delta_days} dias` : ""}).`
      );
      onImported();
    } catch (e) { toast.error(e.message || "Erro a importar"); }
    finally { setBusy(false); }
  };

  return (
    <div
      role="dialog"
      data-testid="import-from-curso-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 text-[#5B21B6] mb-1">
              <Copy className="w-5 h-5" />
              <h3 className="text-base font-semibold">Importar sessões de outro curso</h3>
            </div>
            <p className="text-xs text-[#6E6754]">
              Replica o plano de sessões (datas, horários, sumários, fases pedagógicas) de outro curso já configurado.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="import-curso-modal-close"
            className="text-[#6B7280] hover:text-[#0F1829] -mr-2 -mt-2 p-1"
            aria-label="Fechar"
          ><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Curso origem
            </span>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              disabled={loadingCursos}
              className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white"
              data-testid="import-curso-picker"
            >
              <option value="">— Escolher curso origem —</option>
              {cursos.map((c) => (
                <option key={c.ID_Curso} value={c.ID_Curso}>
                  {c.Descricao || `Produto ${c.ID_Produto}`} • #{c.ID_Curso}{c.ID_Produto != null ? ` (Produto #${c.ID_Produto})` : ""} • {fmtDate(c.DataInicio)}
                </option>
              ))}
            </select>
          </label>

          <label className="inline-flex items-center gap-2 text-sm text-[#6E6754]">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
              data-testid="import-include-past"
            />
            Incluir cursos passados na lista
          </label>

          {preview && (
            <div className="text-xs text-[#3D352B] bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3">
              <p className="font-semibold mb-1">Origem: {preview.length} sessões</p>
              {preview.slice(0, 5).map((s) => (
                <div key={s.ID_PlanoSessao} className="flex justify-between gap-2 py-0.5">
                  <span>Ord {s.Ordem} · {fmtDate(s.Data)}</span>
                  <span className="text-[#978D78] truncate">{s.HoraSessao || "—"}</span>
                </div>
              ))}
              {preview.length > 5 && <p className="text-[#978D78] mt-1">… e mais {preview.length - 5}</p>}
            </div>
          )}

          <div className="border-t border-[#E6E1D6] pt-3 space-y-2">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Opções</label>
            <label className="flex items-start gap-2 text-sm text-[#3D352B]">
              <input
                type="checkbox"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
                data-testid="import-replace"
                className="mt-1"
              />
              <span>
                <strong>Substituir</strong> sessões existentes do destino
                {existingSessoes.length > 0 && (
                  <span className="text-[#B91C1C] ml-1">
                    (apaga {existingSessoes.length} sessão{existingSessoes.length === 1 ? "" : "ões"})
                  </span>
                )}
                <br/>
                <span className="text-[11px] text-[#6E6754]">Caso contrário, é acrescentado ao final mantendo a Ordem.</span>
              </span>
            </label>

            <label className="block">
              <span className="block text-[11px] text-[#3D352B] mb-1">
                Nova data da primeira sessão <span className="text-[#978D78]">(opcional — deixar vazio mantém datas originais)</span>
              </span>
              <input
                type="date"
                value={shiftStart}
                onChange={(e) => setShiftStart(e.target.value)}
                data-testid="import-shift-start"
                className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
              />
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="import-curso-cancel"
          >Cancelar</button>
          <button
            onClick={handleImport}
            disabled={busy || !sourceId || !(preview?.length)}
            data-testid="import-curso-confirm"
            className="flex-1 px-3 py-2 text-sm bg-[#5B21B6] text-white rounded hover:bg-[#4C1D95] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
            Importar {preview?.length || 0} sessões
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Modal: Recalcular datas (shift) ────────────────────────────────
function ShiftDatesModal({ cursoId, sessoes, onClose, onShifted }) {
  const firstSession = sessoes && sessoes.length > 0 ? sessoes[0] : null;
  const lastSession = sessoes && sessoes.length > 0 ? sessoes[sessoes.length - 1] : null;
  const [newDate, setNewDate] = useState(toDateInputValue(firstSession?.Data));
  const [busy, setBusy] = useState(false);

  const oldFirst = firstSession?.Data ? new Date(firstSession.Data) : null;
  const newFirst = newDate ? new Date(newDate) : null;
  const deltaDays =
    oldFirst && newFirst && !Number.isNaN(oldFirst.getTime()) && !Number.isNaN(newFirst.getTime())
      ? Math.round((newFirst - oldFirst) / (1000 * 60 * 60 * 24))
      : 0;

  const previewLastDate = lastSession?.Data && deltaDays !== 0
    ? (() => {
        const d = new Date(lastSession.Data);
        d.setDate(d.getDate() + deltaDays);
        return d;
      })()
    : (lastSession?.Data ? new Date(lastSession.Data) : null);

  const handleShift = async () => {
    if (!newDate) { toast.error("Indica a nova data de início."); return; }
    if (deltaDays === 0) { toast.info("Data igual à actual — nada para alterar."); return; }
    if (!window.confirm(
      `Confirmar deslocação de ${sessoes.length} sessões em ${deltaDays > 0 ? "+" : ""}${deltaDays} dia(s)?\n\n`
      + `Esta acção também actualiza a DataInicio e DataFim do curso.`
    )) return;
    setBusy(true);
    try {
      const r = await adminApi.sessoesShiftDates(cursoId, newDate);
      toast.success(`${r.shifted} sessões deslocadas (${r.delta_days > 0 ? "+" : ""}${r.delta_days} dias). Novo fim: ${r.new_fim}.`);
      onShifted();
    } catch (e) {
      toast.error(e.message || "Erro a deslocar datas");
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog"
      data-testid="shift-dates-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 text-[#1D4ED8] mb-1">
              <CalendarClock className="w-5 h-5" />
              <h3 className="text-base font-semibold">Recalcular datas</h3>
            </div>
            <p className="text-xs text-[#6E6754]">
              Desloca todas as {sessoes.length} sessões mantendo os offsets relativos.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="shift-modal-close"
            className="text-[#6B7280] hover:text-[#0F1829] -mr-2 -mt-2 p-1"
            aria-label="Fechar"
          ><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <div className="text-xs text-[#3D352B] bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3">
            <div className="flex justify-between"><span>Início actual:</span><strong>{fmtDate(firstSession?.Data)}</strong></div>
            <div className="flex justify-between"><span>Fim actual:</span><strong>{fmtDate(lastSession?.Data)}</strong></div>
          </div>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Nova data da primeira sessão
            </span>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              data-testid="shift-new-date"
              className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
            />
          </label>

          {deltaDays !== 0 && previewLastDate && (
            <div className="text-xs text-[#1D4ED8] bg-blue-50 border border-blue-200 rounded p-3">
              <p className="font-semibold mb-1">Previsão</p>
              <div className="flex justify-between"><span>Deslocamento:</span><strong>{deltaDays > 0 ? "+" : ""}{deltaDays} dia(s)</strong></div>
              <div className="flex justify-between"><span>Novo fim:</span><strong>{fmtDate(previewLastDate)}</strong></div>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="shift-cancel"
          >Cancelar</button>
          <button
            onClick={handleShift}
            disabled={busy || !newDate || deltaDays === 0}
            data-testid="shift-confirm"
            className="flex-1 px-3 py-2 text-sm bg-[#1D4ED8] text-white rounded hover:bg-[#1E40AF] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
            Deslocar {sessoes.length} sessões
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Modal: Editor de sessão (todas as fases pedagógicas) ───────────────
function SessaoEditorModal({ cursoId, sessao, onClose, onSaved }) {
  const isNew = !sessao.ID_PlanoSessao;
  const [tab, setTab] = useState("geral"); // geral | int | des | con
  const [form, setForm] = useState({
    Ordem: sessao.Ordem ?? "",
    Data: toDateInputValue(sessao.Data),
    HoraSessao: sessao.HoraSessao ?? "",
    Duracao: sessao.Duracao ?? "",
    Sumario: sessao.Sumario ?? "",
    ObjetivosGerais: sessao.ObjetivosGerais ?? "",
    EstrategiaRemediacao: sessao.EstrategiaRemediacao ?? "",
    Int_ObjetivosEspecificos: sessao.Int_ObjetivosEspecificos ?? "",
    Int_ConteudosProgramaticos: sessao.Int_ConteudosProgramaticos ?? "",
    Int_Metodologia: sessao.Int_Metodologia ?? "",
    Int_Recursos: sessao.Int_Recursos ?? "",
    Int_Atividades: sessao.Int_Atividades ?? "",
    Int_AvaliacaoConhecimentos: sessao.Int_AvaliacaoConhecimentos ?? "",
    Int_Duracao: sessao.Int_Duracao ?? "",
    Des_ObjetivosEspecificos: sessao.Des_ObjetivosEspecificos ?? "",
    Des_ConteudosProgramaticos: sessao.Des_ConteudosProgramaticos ?? "",
    Des_Metodologia: sessao.Des_Metodologia ?? "",
    Des_Recursos: sessao.Des_Recursos ?? "",
    Des_Atividades: sessao.Des_Atividades ?? "",
    Des_AvaliacaoConhecimentos: sessao.Des_AvaliacaoConhecimentos ?? "",
    Des_Duracao: sessao.Des_Duracao ?? "",
    Con_ObjetivosEspecificos: sessao.Con_ObjetivosEspecificos ?? "",
    Con_ConteudosProgramaticos: sessao.Con_ConteudosProgramaticos ?? "",
    Con_Metodologia: sessao.Con_Metodologia ?? "",
    Con_Recursos: sessao.Con_Recursos ?? "",
    Con_Atividades: sessao.Con_Atividades ?? "",
    Con_AvaliacaoConhecimentos: sessao.Con_AvaliacaoConhecimentos ?? "",
    Con_Duracao: sessao.Con_Duracao ?? "",
  });
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    if (!form.Ordem) { toast.error("Indica o nº da sessão (Ordem)"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.Data) payload.Data = null;
      payload.Ordem = Number(payload.Ordem);
      if (isNew) {
        await adminApi.sessoesCreate(cursoId, payload);
        toast.success("Sessão criada.");
      } else {
        await adminApi.sessoesUpdate(cursoId, sessao.ID_PlanoSessao, payload);
        toast.success("Sessão actualizada.");
      }
      onSaved();
    } catch (e) {
      toast.error(e.message || "Erro a guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="sessao-editor-modal">
      <div className="bg-white rounded-lg w-full max-w-[1100px] max-h-[90vh] overflow-hidden flex flex-col">
        <header className="px-5 py-3 border-b border-[#E6E1D6] flex items-center justify-between">
          <h3 className="font-semibold text-[#1B1B1B]">
            {isNew ? "Nova sessão" : `Editar sessão #${sessao.Ordem}`}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-[#FAF8F2] rounded">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-[#E6E1D6] flex gap-1 overflow-x-auto">
          <TabBtn active={tab === "geral"} onClick={() => setTab("geral")} testid="tab-geral">
            <CalendarDays className="w-4 h-4" /> Geral
          </TabBtn>
          {PHASES.map((p) => {
            const Icon = p.icon;
            return (
              <TabBtn
                key={p.prefix}
                active={tab === p.prefix.toLowerCase()}
                onClick={() => setTab(p.prefix.toLowerCase())}
                testid={`tab-${p.prefix.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" /> {p.title}
              </TabBtn>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === "geral" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nº Sessão (Ordem)*">
                <input
                  type="number"
                  value={form.Ordem}
                  onChange={(e) => setField("Ordem", e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-ordem"
                />
              </Field>
              <Field label="Data">
                <input
                  type="date"
                  value={form.Data}
                  onChange={(e) => setField("Data", e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-data"
                />
              </Field>
              <Field label="Horário (ex: 7h45 - 12h30)">
                <input
                  type="text"
                  value={form.HoraSessao}
                  onChange={(e) => setField("HoraSessao", e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-horasessao"
                />
              </Field>
              <Field label="Duração total (ex: 4h45)">
                <input
                  type="text"
                  value={form.Duracao}
                  onChange={(e) => setField("Duracao", e.target.value)}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-duracao"
                />
              </Field>
              <Field label="Objectivos Gerais" span={2}>
                <textarea
                  value={form.ObjetivosGerais}
                  onChange={(e) => setField("ObjetivosGerais", e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-objetivosgerais"
                />
              </Field>
              <Field label="Sumário (texto livre, legacy)" span={2}>
                <textarea
                  value={form.Sumario}
                  onChange={(e) => setField("Sumario", e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-sumario"
                />
              </Field>
              <Field label="Estratégia de Remediação" span={2}>
                <textarea
                  value={form.EstrategiaRemediacao}
                  onChange={(e) => setField("EstrategiaRemediacao", e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                  data-testid="field-estrategiaremediacao"
                />
              </Field>
            </div>
          )}

          {PHASES.map((p) => (
            tab === p.prefix.toLowerCase() && (
              <div key={p.prefix} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PHASE_FIELDS.map((f) => {
                  const fieldKey = `${p.prefix}_${f.key}`;
                  return (
                    <Field key={fieldKey} label={f.label} span={f.textarea ? 2 : 1}>
                      {f.textarea ? (
                        <textarea
                          value={form[fieldKey]}
                          onChange={(e) => setField(fieldKey, e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                          data-testid={`field-${fieldKey.toLowerCase()}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={form[fieldKey]}
                          onChange={(e) => setField(fieldKey, e.target.value)}
                          className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
                          data-testid={`field-${fieldKey.toLowerCase()}`}
                        />
                      )}
                    </Field>
                  );
                })}
              </div>
            )
          ))}
        </div>

        <footer className="px-5 py-3 border-t border-[#E6E1D6] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="btn-cancel-editor"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A] disabled:opacity-50"
            data-testid="btn-save-sessao"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Criar" : "Guardar"}
          </button>
        </footer>
      </div>
    </div>
  );
}


function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 ${
        active
          ? "border-[#A8843C] text-[#A8843C] font-semibold"
          : "border-transparent text-[#6E6754] hover:text-[#1B1B1B]"
      }`}
    >
      {children}
    </button>
  );
}


function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}


// ── Modal: Importação Excel ──────────────────────────────────────────
function ImportSessoesModal({ cursoId, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [scope, setScope] = useState(cursoId ? "current" : "multi"); // current | multi
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!file) { toast.error("Escolhe um ficheiro Excel"); return; }
    setBusy(true);
    try {
      const r = await adminApi.sessoesImportExcel(file, {
        cursoId: scope === "current" ? cursoId : null,
        replace,
      });
      setResult(r);
      toast.success(`Import: ${r.inserted} inseridas, ${r.updated} actualizadas`);
    } catch (e) {
      toast.error(e.message || "Erro no import");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="bg-white rounded-lg w-full max-w-[640px] max-h-[90vh] overflow-hidden flex flex-col">
        <header className="px-5 py-3 border-b border-[#E6E1D6] flex items-center justify-between">
          <h3 className="font-semibold text-[#1B1B1B]">Importar sessões do Excel</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#FAF8F2] rounded">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Ficheiro Excel (.xlsx)
            </label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm"
              data-testid="import-file"
            />
            <p className="text-[11px] text-[#978D78] mt-1">
              Usa os headers oficiais (Id, nr_sessao, DataSessao, HoraSessao, Duracao, ObjetivosGerais,
              Int_*, Des_*, Con_*). Faz download do{" "}
              <a href={adminApi.sessoesTemplateUrl()} className="underline text-[#A8843C]">template vazio</a> para garantir o formato.
            </p>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Destino
            </label>
            <div className="space-y-2 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  checked={scope === "current"}
                  onChange={() => setScope("current")}
                  disabled={!cursoId}
                  data-testid="scope-current"
                />
                <span>
                  <strong>Forçar curso actual</strong> {cursoId && `(#${cursoId})`} — ignora coluna <code className="text-[11px]">Id</code> do Excel.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  checked={scope === "multi"}
                  onChange={() => setScope("multi")}
                  data-testid="scope-multi"
                />
                <span>
                  <strong>Multi-curso</strong> — usa a coluna <code className="text-[11px]">Id</code> do Excel para
                  distribuir as sessões pelos cursos correctos.
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
              Modo
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
                data-testid="mode-replace"
              />
              <span>
                <strong>Substituir todas as sessões</strong> — soft-delete (Activo=N) das sessões
                existentes antes de importar. Por defeito faz UPSERT por (ID_Curso, Ordem).
              </span>
            </label>
          </div>

          {result && (
            <div className="bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3 text-sm space-y-2" data-testid="import-result">
              <div>
                <strong>Processadas:</strong> {result.processed} ·{" "}
                <strong className="text-green-700">Inseridas:</strong> {result.inserted} ·{" "}
                <strong className="text-blue-700">Actualizadas:</strong> {result.updated}
              </div>
              {result.errors?.length > 0 && (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-red-700">{result.errors.length} erros</summary>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    {result.errors.slice(0, 20).map((e, i) => (
                      <li key={i} className="text-red-700">{e}</li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="text-[11px] text-[#978D78]">
                Headers detectados: {(result.headers_detected || []).slice(0, 8).join(", ")}…
              </div>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-[#E6E1D6] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="btn-close-import"
          >
            Fechar
          </button>
          {result ? (
            <button
              onClick={onImported}
              className="px-4 py-2 text-sm bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A]"
              data-testid="btn-finish-import"
            >
              Concluir e recarregar
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!file || busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A] disabled:opacity-50"
              data-testid="btn-submit-import"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Importar
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
